import React, { useEffect, useMemo, useState } from 'react';
import {
  isFreshBoardCursor,
  subscribeBoardPresence,
  type BoardCursorPresence,
} from '../../../utils/boardPresenceService';
import type { FloorPlanViewBox } from './floorPlanMath';
import { planPointToSheetPercent } from './floorPlanPresence';

interface FloorPlanPresenceLayerProps {
  dealId: string;
  selfUid: string;
  nodeId: string;
  viewBox: FloorPlanViewBox;
}

const FloorPlanPresenceLayer: React.FC<FloorPlanPresenceLayerProps> = ({
  dealId,
  selfUid,
  nodeId,
  viewBox,
}) => {
  const [cursors, setCursors] = useState<BoardCursorPresence[]>([]);
  const [, setTick] = useState(0);

  useEffect(() => subscribeBoardPresence(dealId, selfUid, setCursors), [dealId, selfUid]);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const fresh = useMemo(() => {
    const now = Date.now();
    return cursors.filter(
      (c) => c.surface === 'floorplan' && c.nodeId === nodeId && isFreshBoardCursor(c, now),
    );
  }, [cursors, nodeId]);

  if (fresh.length === 0) return null;

  return (
    <div className="fp-presence-layer" aria-hidden>
      {fresh.map((c) => {
        const pos = planPointToSheetPercent(c.x, c.y, viewBox);
        return (
          <div
            key={c.uid}
            className="fp-presence-cursor"
            style={{ left: `${pos.left}%`, top: `${pos.top}%` }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" className="fp-presence-arrow">
              <path
                d="M5.5 3.2L19 11.4l-6.3 1.5-3.2 5.8z"
                fill={c.color}
                stroke="#fff"
                strokeWidth="1.4"
              />
            </svg>
            <span className="fp-presence-name" style={{ background: c.color }}>
              {c.name}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(FloorPlanPresenceLayer);
